/**
 * The Requests workspace's shell state: which page the URL has open (the
 * list, an intake, a submission), the selection the chat sends with each
 * turn, the refetch bus that fans `data-invalidate` parts (and the pages' own
 * writes) out to the views, and the composer hooks rows and menus push
 * prompts through. `open()` is navigation, so Casey's `data-open-artifact`,
 * the nav tree and toasts all land on the same pages.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAgentPanelStore } from "@/features/agents/agentPanelStore";
import { invalidateClient } from "@/features/clientProfile/lib/clientInvalidation";
import { firmKnowledgePath } from "@/features/firmKnowledge/routes";

import { parseTemplateArtifactId, type Stage } from "../model";
import {
  buildListSearch,
  buildOpenSearch,
  parseOpenParam,
  parseStageParam,
  REQUESTS_TAB
} from "../routes";
import {
  artifactKey,
  type RequestArtifact,
  type RequestsInvalidateListener,
  type RequestsInvalidateTarget,
  type RequestsSelection
} from "../types";

export type OpenOptions = {
  /** Swap the current history entry instead of pushing one. */
  replace?: boolean;
};

export type OpenListOptions = OpenOptions & {
  /** Filter the list to one stage; omitted or null shows every request. */
  stage?: Stage | null;
};

export type RequestsIdeContextValue = {
  clientId: string;

  /** The page the URL has open; null on the list. */
  activeArtifact: RequestArtifact | null;
  open: (artifact: RequestArtifact, opts?: OpenOptions) => void;
  /** The stage the list is filtered to (`?stage=`); null for all. */
  listStage: Stage | null;
  /** Back to the Requests list, optionally filtered to a stage. */
  openList: (opts?: OpenListOptions) => void;

  // Selection the chat sends with each turn
  selection: RequestsSelection;
  setSelectedNodeIds: (ids: string[]) => void;
  setSelectedAnswerKey: (key: string | null) => void;

  // Refetch bus
  invalidate: (targets: RequestsInvalidateTarget[]) => void;
  onInvalidate: (listener: RequestsInvalidateListener) => () => void;

  // Composer hooks: rows push mentions, menus and pages seed prompts
  requestMention: (artifact: RequestArtifact) => void;
  onMentionRequest: (
    listener: (artifact: RequestArtifact) => void
  ) => () => void;
  requestPrompt: (text: string, opts?: { send?: boolean }) => void;
  onPromptRequest: (
    listener: (text: string, opts: { send: boolean }) => void
  ) => () => void;
};

const RequestsIdeContext = createContext<RequestsIdeContextValue | null>(null);

type Props = {
  clientId: string;
  children: ReactNode;
};

export const RequestsIdeProvider: FC<Props> = ({ clientId, children }) => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);
  const [selectedAnswerKey, setSelectedAnswerKey] = useState<string | null>(
    null
  );
  /** Titles callers passed to `open`, until the data can name the artifact. */
  const titles = useRef(new Map<string, string>());
  const searchRef = useRef(search);
  searchRef.current = search;
  const invalidateListeners = useRef(new Set<RequestsInvalidateListener>());
  const mentionListeners = useRef(
    new Set<(artifact: RequestArtifact) => void>()
  );
  const promptListeners = useRef(
    new Set<(text: string, opts: { send: boolean }) => void>()
  );

  const onRequestsTab = new URLSearchParams(search).get("tab") === REQUESTS_TAB;
  const opened = onRequestsTab ? parseOpenParam(search) : null;
  const listStage = onRequestsTab ? parseStageParam(search) : null;
  const openedKey = opened ? artifactKey(opened) : null;
  const activeArtifact = useMemo<RequestArtifact | null>(
    () =>
      opened
        ? { ...opened, title: titles.current.get(artifactKey(opened)) ?? "" }
        : null,
    // Keyed on the artifact, not the parsed object.
    [openedKey]
  );

  // A different page starts with nothing selected.
  useEffect(() => {
    setSelectedNodeIds([]);
    setSelectedAnswerKey(null);
  }, [openedKey]);

  const open = useCallback(
    (artifact: RequestArtifact, opts?: OpenOptions) => {
      if (artifact.kind === "template") {
        const { templateId } = parseTemplateArtifactId(artifact.id);
        navigate(firmKnowledgePath({ templateId }));
        return;
      }
      if (artifact.title)
        titles.current.set(artifactKey(artifact), artifact.title);
      const next = buildOpenSearch(searchRef.current, artifact);
      if (next === searchRef.current) return;
      navigate(
        { pathname: `/clients/${encodeURIComponent(clientId)}`, search: next },
        { replace: Boolean(opts?.replace) }
      );
    },
    [clientId, navigate]
  );

  const openList = useCallback(
    (opts?: OpenListOptions) => {
      const next = buildListSearch(searchRef.current, opts?.stage);
      if (next === searchRef.current) return;
      navigate(
        { pathname: `/clients/${encodeURIComponent(clientId)}`, search: next },
        { replace: Boolean(opts?.replace) }
      );
    },
    [clientId, navigate]
  );

  const invalidate = useCallback(
    (targets: RequestsInvalidateTarget[]) => {
      if (!targets.length) return;
      invalidateListeners.current.forEach((listener) => listener(targets));
      // The profile header and badges mount above this provider; reach them
      // through the client bus (it keeps only the client-scoped targets).
      invalidateClient(clientId, targets);
    },
    [clientId]
  );
  const onInvalidate = useCallback((listener: RequestsInvalidateListener) => {
    invalidateListeners.current.add(listener);
    return () => {
      invalidateListeners.current.delete(listener);
    };
  }, []);

  // Casey starts closed and its panel mounts on open, so a prompt or mention
  // asked of it waits here until the panel subscribes.
  const pendingMention = useRef<RequestArtifact | null>(null);
  const pendingPrompt = useRef<{ text: string; send: boolean } | null>(null);

  const requestMention = useCallback((artifact: RequestArtifact) => {
    useAgentPanelStore.getState().setOpen(true);
    if (!mentionListeners.current.size) {
      pendingMention.current = artifact;
      return;
    }
    mentionListeners.current.forEach((listener) => listener(artifact));
  }, []);
  const onMentionRequest = useCallback(
    (listener: (artifact: RequestArtifact) => void) => {
      mentionListeners.current.add(listener);
      const pending = pendingMention.current;
      pendingMention.current = null;
      if (pending) listener(pending);
      return () => {
        mentionListeners.current.delete(listener);
      };
    },
    []
  );
  const requestPrompt = useCallback(
    (text: string, opts?: { send?: boolean }) => {
      const resolved = { send: Boolean(opts?.send) };
      useAgentPanelStore.getState().setOpen(true);
      if (!promptListeners.current.size) {
        pendingPrompt.current = { text, ...resolved };
        return;
      }
      promptListeners.current.forEach((listener) => listener(text, resolved));
    },
    []
  );
  const onPromptRequest = useCallback(
    (listener: (text: string, opts: { send: boolean }) => void) => {
      promptListeners.current.add(listener);
      const pending = pendingPrompt.current;
      pendingPrompt.current = null;
      if (pending) listener(pending.text, { send: pending.send });
      return () => {
        promptListeners.current.delete(listener);
      };
    },
    []
  );

  const selection = useMemo<RequestsSelection>(
    () => ({ artifact: activeArtifact, selectedNodeIds, selectedAnswerKey }),
    [activeArtifact, selectedNodeIds, selectedAnswerKey]
  );

  const value = useMemo<RequestsIdeContextValue>(
    () => ({
      clientId,
      activeArtifact,
      open,
      listStage,
      openList,
      selection,
      setSelectedNodeIds,
      setSelectedAnswerKey,
      invalidate,
      onInvalidate,
      requestMention,
      onMentionRequest,
      requestPrompt,
      onPromptRequest
    }),
    [
      clientId,
      activeArtifact,
      open,
      listStage,
      openList,
      selection,
      invalidate,
      onInvalidate,
      requestMention,
      onMentionRequest,
      requestPrompt,
      onPromptRequest
    ]
  );

  return (
    <RequestsIdeContext.Provider value={value}>
      {children}
    </RequestsIdeContext.Provider>
  );
};

export function useRequestsIde(): RequestsIdeContextValue {
  const ctx = useContext(RequestsIdeContext);
  if (!ctx)
    throw new Error("useRequestsIde must be used within RequestsIdeProvider");
  return ctx;
}

/** Subscribe to invalidations for the given targets; `refetch` runs when any hit. */
export function useOnRequestsInvalidate(
  targets: RequestsInvalidateTarget[],
  refetch: (matched: RequestsInvalidateTarget[]) => void
): void {
  const { onInvalidate } = useRequestsIde();
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  const key = targets.join("|");
  useEffect(() => {
    const wanted = new Set(key.split("|"));
    return onInvalidate((hit) => {
      const matched = hit.filter((t) => wanted.has(t));
      if (matched.length) refetchRef.current(matched);
    });
  }, [key, onInvalidate]);
}

export { artifactKey };
